import React from 'react';
import { WorkspaceApiClient } from '@/infrastructure/workspace/WorkspaceApiClient';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { getLocaleDateFormatTag } from '@/i18n/localeCatalog';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

function localizeNotificationTitle(rawTitle, t) {
  const v = String(rawTitle || '').trim().toLowerCase();
  if (v === 'thông báo hệ thống' || v === 'thong bao he thong' || v === 'system notification') {
    return t('notifications.systemTitle');
  }
  if (v === 'nhắc nhở' || v === 'nhac nho' || v === 'reminder') {
    return t('notifications.reminderTitle');
  }
  return rawTitle || t('notifications.systemTitle');
}

function localizeNotificationType(rawType, t) {
  const v = String(rawType || '').trim().toLowerCase();
  if (v === 'important') return t('notifications.typeImportant');
  if (v === 'reminder') return t('notifications.typeReminder');
  return rawType || t('notifications.typeGeneral');
}

function safeFormatDate(ts, locale, t) {
  const value = ts ? new Date(ts) : null;
  return value && Number.isFinite(value.getTime())
    ? value.toLocaleString(getLocaleDateFormatTag(locale))
    : t('notifications.unknownDate');
}

export default function NotificationsManagementPage({ session }) {
  const { t, locale } = useAppTranslation();
  const api = React.useMemo(() => new WorkspaceApiClient(), []);
  const token = session?.accessToken || null;
  const [notifications, setNotifications] = React.useState([]);
  const [result, setResult] = React.useState('');

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.getNotifications(token);
      setNotifications(data?.items || []);
    } catch (e) {
      setResult(formatApiErrorMessage(e?.message, t) || t('errors.loadNotifications'));
    }
  }, [api, token, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-headline text-3xl font-extrabold">{t('notifications.pageTitle')}</h1>
        <button
          onClick={async () => {
            if (!token) return;
            await api.markAllNotificationsRead(token);
            await load();
          }}
          className="rounded-lg border border-cyan-300 px-3 py-2 text-sm font-bold text-cyan-700"
        >
          {t('notifications.markAllRead')}
        </button>
      </div>

      <div className="space-y-3">
        {notifications.map((item) => (
          <article key={item.id} className={`rounded-xl border p-4 shadow-sm ${
            item.read
              ? 'border-slate-100 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              : 'border-cyan-100 bg-cyan-50 text-slate-800 dark:border-cyan-800 dark:bg-slate-900'
          }`}>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <h3 className="font-bold">{localizeNotificationTitle(item.title, t)}</h3>
                <p className="text-xs uppercase tracking-wide opacity-70">{localizeNotificationType(item.type, t)}</p>
              </div>
              <span className="text-xs opacity-70">
                {safeFormatDate(item.createdAt, locale, t)}
              </span>
            </div>
            <p className="text-sm">{item.message}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.actionLabel ? (
                <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold dark:border-slate-700">
                  {t('notifications.action', { label: item.actionLabel })}
                </button>
              ) : null}
              {!item.read ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!token) return;
                    await api.markNotificationRead(item.id, token);
                    await load();
                  }}
                  className="rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-bold text-emerald-700"
                >
                  {t('notifications.markRead')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={async () => {
                  if (!token) return;
                  await api.deleteNotification(item.id, token);
                  await load();
                }}
                className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-bold text-rose-700"
              >
                {t('notifications.delete')}
              </button>
            </div>
          </article>
        ))}
      </div>
      {result ? <p className="text-sm font-semibold text-rose-700">{result}</p> : null}
    </div>
  );
}
